import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';

// This URL must be configured in your .env file
// and should point to the application where users
// will be redirected after a successful login.
const REDIRECT_URL = process.env.REACT_APP_REDIRECT_URL;
const API_URL = process.env.REACT_APP_API_URL;
const SESSION_KEY = 'kendall_session_forced';
const LOGIN_ATTEMPT_KEY = 'kendall_login_attempt_timestamp';

function AuthApp() {
  const {
    loginWithRedirect,
    isAuthenticated,
    isLoading,
    error,
    user,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
  } = useAuth0();
  
  const [apiError, setApiError] = useState(null);
  const [stores, setStores] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    // Check if we're stuck in a login loop (user pressed back button)
    const checkForStuckLogin = () => {
      const loginAttemptTime = sessionStorage.getItem(LOGIN_ATTEMPT_KEY);
      const sessionKeyValue = isPopup ? '1-popup' : '1-window';
      const hasSessionKey = sessionStorage.getItem(SESSION_KEY) === sessionKeyValue;
      
      if (loginAttemptTime && hasSessionKey && !isLoading && !isAuthenticated) {
        const timeSinceAttempt = Date.now() - parseInt(loginAttemptTime);
        // If more than 10 seconds have passed since login attempt and we're still not authenticated
        if (timeSinceAttempt > 10000) {
          // Clear the session keys to allow retry
          sessionStorage.removeItem(SESSION_KEY);
          sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
          return true; // Indicates we cleared the stuck state
        }
      }
      return false;
    };
    
    const registerUserInBackend = async () => {
      if (!isAuthenticated || !user || !API_URL) {
        return;
      }

      try {
        let token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE }
        });

        // Check if we received a store_id parameter in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const storeId = urlParams.get('store_id');
        
        const params = { user };
        if (storeId) {
          params.store_id = storeId;
        }

        const response = await fetch(`${API_URL}/register-user`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user }),
        });

        if (!response.ok) {
          let errorMessage = `API Error: ${response.statusText}`;
          try {
            const errorBody = await response.json();
            errorMessage = errorBody.message || JSON.stringify(errorBody);
          } catch (e) {
            // Ignore if response body is not JSON
          }
          throw new Error(errorMessage);
        }

        if (!storeId && !isPopup) {
          try {
            const storesResponse = await fetch(`${API_URL}/fetch-user-stores`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ auth0_user_id: user.sub }),
            });

            if (storesResponse.ok) {
              const storesData = await storesResponse.json();
              
              if (storesData && storesData.length > 0) {
                setStores(storesData);
                return;
              }
            } else {
              console.error('API Error fetching stores:', await storesResponse.text());
            }
          } catch (e) {
            console.error('Failed to fetch stores:', e);
          }
        }
 
        // Check if the app is running inside a popup window
        if (window.opener && REDIRECT_URL) {
          // Popup flow - keep existing behavior for future use
          sessionStorage.removeItem(SESSION_KEY);
          const message = {
              type: 'custom-auth-success',
              auth0_user_id: user.sub // 'user.sub' is the auth0_user_id
          };
          
          // Send message back to the main window (Kendall UI)
          window.opener.postMessage(message, REDIRECT_URL);
          window.close();
        } else {
          // Regular flow - show confirmation screen (no automatic redirect)
          sessionStorage.removeItem(SESSION_KEY);
          setShowConfirmation(true);
        }

      } catch (e) {
        console.error('Failed to register user in backend:', e);
        setApiError(e.message);
      }
    };
    
    if (error) {
      // This is a top-level Auth0 error, not an API error
      return;
    }

    const isPopup = !!window.opener;
    const sessionKeyValue = isPopup ? '1-popup' : '1-window';
    
    // Check for stuck login state first
    checkForStuckLogin();
    
    // Always force credentials once per popup open (ignore Auth0's current session)
    if (!isLoading && sessionStorage.getItem(SESSION_KEY) !== sessionKeyValue) {
      sessionStorage.setItem(SESSION_KEY, sessionKeyValue);
      sessionStorage.setItem(LOGIN_ATTEMPT_KEY, Date.now().toString());
      
      loginWithRedirect({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
          prompt: 'login',
          max_age: 0,
        }
      });
      return;
    }

    if (!isLoading && isAuthenticated) {
      // Clear login attempt timestamp on successful authentication
      sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
      registerUserInBackend();
      return; 
    }

    // No-op: the forced interactive login above covers the unauthenticated case
  }, [isAuthenticated, isLoading, loginWithRedirect, error, user, getAccessTokenSilently, getIdTokenClaims]);

  // Show confirmation screen after successful registration/login
  if (showConfirmation) {
    return (
      <div className="container">
        <div className="card">
          <img src="/logo.svg" className="logo" alt="logo" />
          <div className="confirmation-content">
            <h2>Welcome to Kendall!</h2>
            <p>You have successfully authenticated with your account.</p>
            <p className="email-note"><strong>{user?.email}</strong></p>
            
            <div className="welcome-message">
              <h3>No Stores Available</h3>
              <p>Your account is not currently assigned to any stores. To get access, please contact our support team.</p>
              
              <div className="contact-info">
                <p><strong>Email:</strong> support@kendall.ai</p>
                <p><strong>Subject:</strong> Stores Access Request - {user?.email}</p>
              </div>
              
              <p className="help-text">
                Our team will help you get access to the appropriate stores and configure your permissions in Kendall's analytics platform.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stores.length > 0 && REDIRECT_URL) {
    sessionStorage.removeItem(SESSION_KEY); // Remove the session key so that the next login will be forced
    const redirectBaseUrl = new URL(REDIRECT_URL).origin;

    return (
      <div className="container">
        <div className="card">
          <img src="/logo.svg" className="logo" alt="logo" />
          <h2>Select your store</h2>
          <div className="stores-list">
            {stores.map((store) => {
              let storeUrl = `${redirectBaseUrl}/auth/forcelogin?store_id=${store.id}&secret=${store.secret}&auth0_user_id=${user.sub}`;
              if (store.data_view_id) {
                storeUrl += `&data_view_id=${store.data_view_id}`;
              }
              return (
                <a href={storeUrl} key={store.id} className="store-item">
                  {store.logo ? (
                    <img src={store.logo} alt={store.name} className="store-logo" />
                  ) : (
                    <div className="store-avatar">
                      <span>{store.name.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  <span className="store-name">{store.name}</span>
                </a>
              );
            })}
          </div>
          <div className="card-footer">
            <span>Logged in as {user?.email || user?.name}</span>
            <span>·</span>
            <button
              className="logout-link"
              onClick={() => {
                try {
                  sessionStorage.removeItem(SESSION_KEY);
                } catch (e) {
                  console.error('⚠️ Error removing session key:', e);
                }
                
                const logoutParams = { logoutParams: { returnTo: window.location.origin } };
                logout(logoutParams);
              }}
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Display messages to the user based on the current state.
  let content;
  let showSpinner = false;

  if (apiError) {
    content = (
      <div>
        <p style={{ color: '#E53E3E', fontWeight: 'bold' }}>Could not complete login</p>
        <p>An error occurred while finalizing your session. Please try again later.</p>
        <p><code style={{ color: 'white' }}>Error: {apiError}</code></p>
      </div>
    );
  } else if (error) {
    content = <p>Authentication Error: {error.message}. Please try again.</p>;
  } else if (isAuthenticated && !REDIRECT_URL) {
    content = (
      <div>
        <p>Authentication successful.</p>
        <p>
          Please configure the <code>REACT_APP_REDIRECT_URL</code> environment
          variable to complete the flow.
        </p>
      </div>
    );
  } else if (isLoading) {
    showSpinner = true;
    content = <p>Loading session...</p>;
  } else if (isAuthenticated) {
    showSpinner = true;
    // Since we force login every time, user is always returning from Auth0
    content = <p>Just a moment...</p>;
  } else {
    showSpinner = true;
    content = <p>Redirecting to login...</p>;
  }

  return (
    <div className="container">
      <div className="card">
        <img src="/logo.svg" className="logo" alt="logo" />
        {showSpinner && <div className="spinner"></div>}
        <div className="status-text">{content}</div>
      </div>
    </div>
  );
}

export default AuthApp;
